import React, {useState, useCallback, useRef, useEffect} from 'react';
import clsx from 'clsx';

import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import PermissionItemMenu, {
	type PermissionItemMenuProps,
} from '../ListItemMenu';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';

export type PermissionItemFocusTrapMenuProps = Omit<
	PermissionItemMenuProps,
	'onOpen' | 'onClose'
> & {
	canTab?: boolean;
	index?: number;
	focused?: boolean;
	onBlur?: (event: React.FocusEvent) => void;
	onOpen?: (requested: boolean) => void;
	onClose?: (requested: boolean) => void;
};

const styles = ({palette}: Theme) =>
	createStyles({
		button: {
			'&$active, &:focus': {
				backgroundColor: palette.action.hover,
			},
		},
		active: {},
	});

export const PermissionItemFocusTrapMenu = ({
	classes,
	canTab,
	focused,
	onOpen,
	onClose,
	onBlur,
	open,
	...props
}: PermissionItemFocusTrapMenuProps & WithStyles<typeof styles>) => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const requestedCloseRef = useRef(false);
	const requestedOpenRef = useRef(false);

	const [menuOpen, setMenuOpen] = useState(open);

	useEffect(() => {
		requestedCloseRef.current = open === false;
		requestedOpenRef.current = open === true;
		setMenuOpen(open);
	}, [open]);

	useWhenChangedEffect(
		menuOpen,
		useCallback(
			(menuOpen) => {
				menuOpen
					? onOpen?.(requestedOpenRef.current)
					: onClose?.(requestedCloseRef.current);
				requestedCloseRef.current = requestedOpenRef.current = false;
			},
			[onClose, onOpen],
		),
	);

	const handleMenuToggled = useCallback(
		(open: boolean) => () => {
			setMenuOpen(open);
		},
		[],
	);

	useEffect(() => {
		// If we can no longer initiate focus, is no longer focused, or the menu is open:
		// trigger `blur` on button
		if (!focused || !canTab || menuOpen) {
			buttonRef.current?.blur();
			return;
		}
		// Otherwise, if focused, trigger `focus` on the button
		focused && buttonRef.current?.focus();
	}, [canTab, focused, menuOpen]);

	return (
		<PermissionItemMenu
			{...props}
			onClose={handleMenuToggled(false)}
			onOpen={handleMenuToggled(true)}
			open={menuOpen}
			ButtonProps={{
				...props.ButtonProps,
				ref: buttonRef,
				onBlur,
				tabIndex: canTab && focused && !menuOpen ? 0 : -1,
				className: clsx(classes.button, {
					[classes.active]: canTab && focused && !menuOpen,
				}),
			}}
		/>
	);
};

export default withStyles(styles)(PermissionItemFocusTrapMenu) as (
	props: Omit<PermissionItemFocusTrapMenuProps, 'classes'>,
) => JSX.Element;
